import React, { useState, useEffect } from "react";
import { FileText } from "lucide-react";
import Button from "../../../components/core/Element/Button/Button";
import { RestaurantService } from "../../../utils/api.service";
import { getStoreInfo } from "../../../utils/_hooks";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/reducers/toasterSlice";
import { hideSpinner, showSpinner } from "../../../store/reducers/spinnerSlice";

const Pages = () => {
  const dispatch = useDispatch();
  const [pages, setPages] = useState([
    { id: "home", title: "Home / Landing Page", enabled: true, isDefault: true },
    { id: "about", title: "About Us Section", enabled: true, isDefault: false },
    { id: "menus", title: "Menu Listing & Categories", enabled: true, isDefault: false },
    { id: "tables", title: "Table Reservation / QR Ordering", enabled: true, isDefault: false },
    { id: "contact", title: "Contact Us & Operating Hours", enabled: true, isDefault: false },
  ]);

  useEffect(() => {
    fetchRestaurantData();
  }, []);

  const fetchRestaurantData = async () => {
    try {
      const restaurantId = getStoreInfo()?._id;
      if (!restaurantId) return;

      const response = await RestaurantService.getRestaurantById(restaurantId);
      const visibleSections = response?.data?.visibleSections;

      if (visibleSections) {
        setPages((prevPages) =>
          prevPages.map((p) => ({
            ...p,
            enabled: visibleSections[p.id] !== undefined ? visibleSections[p.id] : true,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching page settings:", error);
    }
  };

  const togglePage = (id) => {
    setPages((prevPages) =>
      prevPages.map((p) => (p.id === id && !p.isDefault ? { ...p, enabled: !p.enabled } : p))
    );
  };

  const handleSaveNavigation = async () => {
    try {
      dispatch(showSpinner());
      const restaurantId = getStoreInfo()?._id;
      if (!restaurantId) {
        dispatch(
          showToast({ type: "error", title: "Error", message: "Restaurant ID not found." })
        );
        return;
      }

      const visibleSections = pages.reduce((acc, curr) => {
        acc[curr.id] = curr.enabled;
        return acc;
      }, {});

      const response = await RestaurantService.updateRestaurant(restaurantId, {
        visibleSections,
      });

      if (response?.status === 200 || response?.status === 201) {
        dispatch(
          showToast({
            type: "success",
            title: "Success",
            message: "Navigation settings saved successfully!",
          })
        );
      }
    } catch (error) {
      console.error("Save Navigation Error:", error);
      dispatch(
        showToast({
          type: "error",
          title: "Error",
          message:
            error?.response?.data?.message || "Failed to save navigation settings.",
        })
      );
    } finally {
      dispatch(hideSpinner());
    }
  };

  return (
    <div className="d-flex flex-column gap-4 bg-light mt-3 p-3 rounded">
      <div>
        <h3>Store Pages & Navigation</h3>
        <p className="text-muted">
          Manage visible page sections displayed on your customer portal.
        </p>
      </div>

      <div className="card p-3 border-0 shadow-sm">
        <div className="list-group list-group-flush">
          {pages.map((page) => (
            <div
              key={page.id}
              className="list-group-item d-flex align-items-center justify-content-between py-3 px-0 border-bottom"
            >
              <div className="d-flex align-items-center gap-3">
                <FileText className="text-primary" size={20} />
                <div>
                  <h6 className="mb-0 fw-semibold">{page.title}</h6>
                  <small className="text-muted">
                    {page.isDefault ? "Required Core Page" : "Optional Section"}
                  </small>
                </div>
              </div>

              <div className="d-flex align-items-center gap-3">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={page.enabled}
                    disabled={page.isDefault}
                    onChange={() => togglePage(page.id)}
                    style={{ cursor: page.isDefault ? "not-allowed" : "pointer" }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4">
          <Button label="Save Navigation" onClick={handleSaveNavigation} />
        </div>
      </div>
    </div>
  );
};

export default Pages;
